/**
 * Widget cache (stale-while-revalidate via localStorage)
 *
 * 動機：app 啟動時 SystemContext / AuthContext / TimerContext 各自打初始 API，
 * 一次 burst ~13 支。其中 software-name / projects / recent-favorites 等
 * 「短期內幾乎不變」的資料每次重整都重打、白白等網路 + 後端排隊。
 *
 * 機制：
 *   1) 啟動時 widgetCache.get(key) 拿到上次的值 → 立刻 setState（首屏就有資料）
 *   2) 同時 fire 網路請求 → 完成後 widgetCache.set + setState（更新最新資料）
 *   3) Mutation 發生（改 software name / 自己改 profile）→ widgetCache.invalidate
 *
 * 不在這裡實作 TTL：state 一旦設好不會被 cache 主動覆蓋；下次重整才從 cache 取。
 * 想嚴格要新鮮資料的 caller 可以自己看 ageMs 決定要不要先用 cache。
 */

const PREFIX = 'widget-cache:'

interface CachedItem<T> {
  v: T
  t: number
}

export const widgetCache = {
  get<T>(key: string): { value: T; ageMs: number } | null {
    try {
      const raw = localStorage.getItem(PREFIX + key)
      if (!raw) return null
      const parsed = JSON.parse(raw) as CachedItem<T>
      if (parsed?.v === undefined) return null
      return { value: parsed.v, ageMs: Date.now() - (parsed.t || 0) }
    } catch {
      return null
    }
  },
  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(PREFIX + key, JSON.stringify({ v: value, t: Date.now() }))
    } catch {
      // 滿了 / 隱私模式 → 靜默
    }
  },
  invalidate(key: string): void {
    try {
      localStorage.removeItem(PREFIX + key)
    } catch {
      /* noop */
    }
  },
  clearAll(): void {
    try {
      const toRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && k.startsWith(PREFIX)) toRemove.push(k)
      }
      toRemove.forEach(k => localStorage.removeItem(k))
    } catch {
      /* noop */
    }
  },
}

// 集中管理 cache key 避免拼錯
export const CACHE_KEYS = {
  softwareName: 'software-name',
  authUser: 'auth-user',
  projectsActive: 'projects-active',
  recentFavorites: 'recent-favorites',
} as const
